<script setup>
import { isNull } from 'lodash'
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCheckinValueField } from '@/utils/check-ins/check-ins'
import {
  NUMBER_INPUT_DIGITS,
  NUMBER_INPUT_FRACTIONS
} from '@/utils/components-configurations/app-input-number'
import { REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { isStringEmpty } from '@/utils/general'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { useKPI, useMetricType, useMetricValues } from '@/utils/okr-element-composables'
import { numberOrNullProp, stringOrNullProp } from '@/utils/prop-validators'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import BinaryValueSwitch from '@/components/form/element-progress-preview/BinaryValueSwitch'
import ProgressPreviewValueField from '@/components/form/element-progress-preview/ProgressPreviewValueField'
import ElementDataSourceInfo from '@/components/form/ElementDataSourceInfo'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppInputNumberNext from '@/components/ui/AppInputNumberNext/AppInputNumberNext'

defineOptions({
  name: 'CheckinValuesField'
})

const props = defineProps({
  okrElement: {
    type: Object,
    required: true
  },

  selectedDate: {
    type: [String, Date, Number],
    default: null
  }
})

const { t } = useI18n()

const currentValueModel = defineModel('currentValue', {
  required: true,
  validator: v => numberOrNullProp(v) || stringOrNullProp(v),
  set: v => v
})

const { FIELD_TYPE_ID } = REQUEST_ENTITY_KEYS

const { isBinary, isThreshold, isStartTarget } = useMetricType({
  element: () => {
    return {
      ...props.okrElement,
      [FIELD_TYPE_ID]: props.okrElement[FIELD_TYPE_ID]
    }
  }
})

const { unit, getOriginalValue, getTargetValue, getThresholdPrefix } = useMetricValues({
  element: () => props.okrElement
})

const { getRelevantThresholdTargetValue } = useKPI({
  element: () => props.okrElement,
  selectedDate: () => props.selectedDate
})

const isValueFieldDisabled = computed(() => {
  return props.okrElement[OKR_ELEMENT_KEYS.EXIST_CONTRIBUTE_CHILDREN]
})

const { isValueFieldDisabledByDataSourceQuery } = useCheckinValueField({
  okrElement: () => props.okrElement
})

const getLocalCurrentValue = computed({
  get: () => {
    return currentValueModel.value ?? ''
  },
  set: newValue => {
    if (isStringEmpty(String(newValue))) {
      currentValueModel.value = null
    } else {
      currentValueModel.value = newValue
    }
  }
})

const currentValueInputRef = useTemplateRef('current-value-input')

watch(
  currentValueInputRef,
  async newValue => {
    // for start/target type focus on current value
    const shouldFocusByFieldTypeId = [
      KR_VALUE_TYPES.START_TARGET,
      KR_VALUE_TYPES.THRESHOLD
    ].includes(props.okrElement[FIELD_TYPE_ID])

    const shouldFocus =
      newValue &&
      shouldFocusByFieldTypeId &&
      !isValueFieldDisabled.value &&
      !isValueFieldDisabledByDataSourceQuery.value
    await nextTick()

    if (shouldFocus) {
      requestAnimationFrame(() => {
        newValue.focus()

        if (
          !isNull(getLocalCurrentValue.value) &&
          !isStringEmpty(String(getLocalCurrentValue.value))
        ) {
          newValue.selectAll()
        }
      })
    }
  },
  { once: true }
)
</script>

<template>
  <div class="cvf-ValuesWrapper">
    <div
      :class="{
        'cvf-Values-threshold': isThreshold
      }"
      class="cvf-Values"
    >
      <FormFieldNext
        v-if="isStartTarget"
        :label="t('create.keyResult.label.originalValue')"
        class="cvf-ValueField"
        disabled
      >
        <ProgressPreviewValueField
          :model-value="getOriginalValue"
          :suffix="unit"
          class="cvf-Input"
          data-auto-testid="start-value"
          data-testid="start-value"
        />
      </FormFieldNext>

      <FormFieldNext
        v-tippy="{
          content: isValueFieldDisabled ? t('current_value.disabled_tooltip') : ''
        }"
        :class="{
          'cvf-ValueField': true,
          'cvf-ValueField-binary': isBinary
        }"
        :disabled="isValueFieldDisabled || isValueFieldDisabledByDataSourceQuery"
        :label="t('common.value')"
      >
        <BinaryValueSwitch
          v-if="isBinary"
          v-model="currentValueModel"
          :disabled="isValueFieldDisabled || isValueFieldDisabledByDataSourceQuery"
          name="checkin-form-binary-type-switch"
        />

        <AppInputNumberNext
          v-else
          ref="current-value-input"
          v-model="getLocalCurrentValue"
          :digit-max-length="NUMBER_INPUT_DIGITS.ELEMENT_VALUES"
          :fraction="NUMBER_INPUT_FRACTIONS.ELEMENT_VALUES"
          :readonly="isValueFieldDisabled || isValueFieldDisabledByDataSourceQuery"
          :suffix="unit"
          allow-empty-value
          allow-negative
          class="cvf-Input"
          data-auto-testid="current-value"
          full-width
          show-previous-value
        />
      </FormFieldNext>

      <template v-if="isThreshold">
        <FormFieldNext :label="t('objectives.threshold')" class="cvf-ValueField" disabled>
          <ProgressPreviewValueField
            :model-value="getRelevantThresholdTargetValue"
            :prefix="getThresholdPrefix"
            :suffix="unit"
            class="cvf-Input"
            data-auto-testid="target-value"
            data-testid="target-value-field"
          />
        </FormFieldNext>
      </template>

      <FormFieldNext
        v-if="isStartTarget"
        :label="t('create.keyResult.label.targetValue')"
        class="cvf-ValueField"
        disabled
      >
        <ProgressPreviewValueField
          :model-value="getTargetValue"
          :suffix="unit"
          class="cvf-Input"
          data-auto-testid="target-value"
          data-testid="target-value-field"
        />
      </FormFieldNext>
    </div>
    <ElementDataSourceInfo
      v-if="isValueFieldDisabledByDataSourceQuery"
      :data-source-query="props.okrElement[OKR_ELEMENT_KEYS.DATA_SOURCE_QUERY]"
    />
  </div>
</template>

<style lang="scss" scoped>
.cvf-ValuesWrapper {
  display: flex;
  gap: 24px;
  flex-direction: column;
}

.cvf-Values {
  display: flex;
  gap: 12px;
  width: 100%;

  --field-width: 157px;

  &-threshold {
    --field-width: 50%;
  }
}

.cvf-ValueField {
  width: var(--field-width);

  &-binary {
    width: 100%;
    min-width: 495px;
  }
}

.cvf-Input {
  --font-size: #{$fs-14};
}
</style>
